import React, { useCallback, useEffect, useRef, useState } from "react";
import { Eye, X, ZoomIn, ZoomOut } from "lucide-react";

const PREVIEW_WIDTH = 640;

export default function ImageCropDialog({ file, aspectRatio, title, onCancel, onSave }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [naturalAspectRatio, setNaturalAspectRatio] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cropAspectRatio = aspectRatio || naturalAspectRatio;
  const previewWidth = Math.min(PREVIEW_WIDTH, 480 * cropAspectRatio);
  const previewHeight = Math.round(previewWidth / cropAspectRatio);

  useEffect(() => {
    const imageUrl = URL.createObjectURL(file);
    const nextImage = new Image();
    nextImage.onload = () => {
      setNaturalAspectRatio(nextImage.width / nextImage.height);
      setImage(nextImage);
    };
    nextImage.onerror = () => setError("This image could not be opened. Please choose another file.");
    nextImage.src = imageUrl;
    return () => URL.revokeObjectURL(imageUrl);
  }, [file]);

  const getLayout = useCallback(() => {
    if (!image) return null;
    const scale = Math.max(previewWidth / image.width, previewHeight / image.height) * zoom;
    const width = image.width * scale;
    const height = image.height * scale;
    const x = Math.min(0, Math.max(previewWidth - width, (previewWidth - width) / 2 + offset.x));
    const y = Math.min(0, Math.max(previewHeight - height, (previewHeight - height) / 2 + offset.y));
    return { scale, width, height, x, y };
  }, [image, offset, previewHeight, previewWidth, zoom]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const layout = getLayout();
    if (!canvas || !image || !layout) return;
    canvas.width = previewWidth;
    canvas.height = previewHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, previewWidth, previewHeight);
    context.drawImage(image, layout.x, layout.y, layout.width, layout.height);
  }, [getLayout, image, previewHeight, previewWidth]);

  const onPointerDown = (event) => {
    const layout = getLayout();
    if (!image || !layout) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      offset: {
        x: layout.x - (previewWidth - layout.width) / 2,
        y: layout.y - (previewHeight - layout.height) / 2,
      },
    };
  };

  const onPointerMove = (event) => {
    if (!dragRef.current || !image) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = previewWidth / bounds.width;
    const scale = Math.max(previewWidth / image.width, previewHeight / image.height) * zoom;
    const maxOffsetX = Math.max(0, (image.width * scale - previewWidth) / 2);
    const maxOffsetY = Math.max(0, (image.height * scale - previewHeight) / 2);
    setOffset({
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, dragRef.current.offset.x + (event.clientX - dragRef.current.x) * ratio)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, dragRef.current.offset.y + (event.clientY - dragRef.current.y) * ratio)),
    });
  };

  const saveCrop = async () => {
    const layout = getLayout();
    if (!image || !layout) return;
    setSaving(true);
    setError("");
    const output = document.createElement("canvas");
    const maxOutputDimension = cropAspectRatio === 1 ? 1200 : 1600;
    const outputWidth = Math.min(maxOutputDimension, Math.round(maxOutputDimension * cropAspectRatio));
    const outputHeight = Math.round(outputWidth / cropAspectRatio);
    output.width = outputWidth;
    output.height = outputHeight;
    const context = output.getContext("2d");
    if (!context) {
      setError("The edited image could not be prepared. Please try another image.");
      setSaving(false);
      return;
    }
    if (file.type === "image/jpeg") {
      context.fillStyle = "#fff";
      context.fillRect(0, 0, outputWidth, outputHeight);
    }
    const sourceX = -layout.x / layout.scale;
    const sourceY = -layout.y / layout.scale;
    const sourceWidth = previewWidth / layout.scale;
    const sourceHeight = previewHeight / layout.scale;
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight,
    );
    const outputType = ["image/jpeg", "image/png", "image/webp"].includes(file.type)
      ? file.type
      : "image/jpeg";
    output.toBlob((blob) => {
      if (!blob) {
        setError("The edited image could not be prepared. Please try another image.");
        setSaving(false);
        return;
      }
      onSave(new File([blob], file.name, { type: blob.type || outputType, lastModified: Date.now() }));
    }, outputType, 0.9);
  };

  return (
    <div
      role="presentation"
      onClick={(event) => { if (event.target === event.currentTarget && !saving) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 10000, display: "grid", placeItems: "center",
        padding: 16, background: "rgba(0,0,0,0.72)", overflowY: "auto",
      }}
      data-testid="image-crop-dialog"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="image-crop-title"
        className="card"
        style={{
          width: "min(100%, 720px)", maxHeight: "calc(100vh - 32px)", overflowY: "auto",
          padding: 20, background: "var(--bg1)", boxShadow: "var(--shadow-lg)",
        }}
      >
        <div className="flex justify-between items-center mb-12">
          <div>
            <h2 id="image-crop-title" className="font-serif fs-20 fw-700">{title}</h2>
            <div className="text-muted fs-12 mt-4">Drag to reposition, then adjust the zoom.</div>
          </div>
          <button type="button" className="btn btn-ghost btn-xs" onClick={onCancel} disabled={saving} aria-label="Cancel image editing">
            <X size={18} />
          </button>
        </div>

        <div style={{
          maxWidth: "100%", width: `min(100%, ${previewWidth}px)`, aspectRatio: `${cropAspectRatio}`, margin: "0 auto 16px",
          overflow: "hidden", borderRadius: 12, background: "#171717", touchAction: "none",
        }}>
          <canvas
            ref={canvasRef}
            aria-label="Image crop preview. Drag the image to change the crop."
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={() => { dragRef.current = null; }}
            onPointerCancel={() => { dragRef.current = null; }}
            style={{ width: "100%", height: "100%", display: "block", cursor: image ? "grab" : "wait" }}
            data-testid="image-crop-preview"
          />
        </div>

        <div className="flex items-center gap-12 mb-16">
          <ZoomOut size={18} aria-hidden="true" />
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            aria-label="Zoom image"
            style={{ flex: 1 }}
            data-testid="image-crop-zoom"
          />
          <ZoomIn size={18} aria-hidden="true" />
          <span className="fs-12 fw-600" style={{ minWidth: 42, textAlign: "right" }}>{Math.round(zoom * 100)}%</span>
        </div>

        {error && <div role="alert" className="fs-12 mb-12" style={{ color: "var(--red)" }}>{error}</div>}

        <div className="flex justify-between items-center gap-12">
          <div className="text-muted fs-11 flex items-center gap-4"><Eye size={14} /> Preview updates as you edit</div>
          <div className="flex gap-8">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="button" className="btn btn-gold btn-sm" onClick={saveCrop} disabled={!image || saving} data-testid="image-crop-save">
              {saving ? "Preparing…" : "Use this crop"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
